import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { reportPageStyles as styles } from '../styles';
import { inputSections } from '../../../configs/FinancialInputConfig';
import { ReportTypes } from '../../../propTypes';
import { COLOR_GREY_600, COLOR_TEXT_1 } from 'src/colors';
import { InputTextField } from 'src/App/Components/Inputs';

const InputEditModal = ({
  isOpen,
  onClose,
  currency,
  stateObj,
  handleValueChange,
  reportType,
  handleApply,
}: {
  isOpen: boolean;
  onClose: () => void;
  currency: string;
  stateObj: any;
  handleValueChange: (key: string, value: any) => void;
  reportType: ReportTypes;
  handleApply: () => void;
}) => {
  // Render input fields dynamically
  const renderFields = (fields: Record<string, any>, disabled: boolean = false) => {
    return Object.entries(fields).map(([key, field]) => {
      if (field.isDropdown) {
        return (
          <Grid item xs={6} key={key}>
            <Autocomplete
              disablePortal
              options={field.dropdownOptions || []}
              renderInput={params => <TextField {...params} label={field.label} />}
              value={stateObj[key] || ''}
              onChange={(event, value) => handleValueChange(key, value)}
              disabled={disabled}
            />
          </Grid>
        );
      } else {
        return (
          <Grid item xs={6} key={key}>
            <InputTextField
              field={{ ...field, key }}
              stateObj={stateObj}
              handleValueChange={handleValueChange}
              currency={currency}
              disabled={disabled}
            />
          </Grid>
        );
      }
    });
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          ...styles.paper,
          ...styles.modalCenter,
          width: 'clamp(300px, 70%, 600px)',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '30px',
        }}
      >
        <IconButton onClick={onClose} sx={styles.filterModalCloseIcon}>
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            overflowY: 'auto',
            flex: 1,
          }}
        >
          <Grid container spacing={4}>
            {Object.entries(inputSections).map(([sectionKey, sectionConfig]) => {
              const isSectionAllowed = sectionConfig.allowedModes.includes(reportType);

              if (!isSectionAllowed) {
                return null;
              }

              const isSectionDisabled =
                !isSectionAllowed ||
                (sectionConfig.canToggle &&
                  !(sectionConfig.toggleKey && stateObj[sectionConfig.toggleKey]));

              return (
                <Grid container item key={sectionKey} spacing={3}>
                  <Grid container item xs={12} alignItems="center">
                    <Typography
                      variant="h2"
                      sx={{ color: !isSectionAllowed ? COLOR_GREY_600 : COLOR_TEXT_1 }}
                    >
                      {sectionConfig.displayName}
                      {sectionConfig.canToggle && (
                        <Switch
                          checked={
                            sectionConfig.toggleKey
                              ? stateObj[sectionConfig.toggleKey] || false
                              : false
                          }
                          onChange={(event, checked) =>
                            handleValueChange(sectionConfig.toggleKey || '', checked)
                          }
                          disabled={!isSectionAllowed}
                        />
                      )}
                    </Typography>
                  </Grid>

                  <Grid container item spacing={2}>
                    {renderFields(sectionConfig.fields, isSectionDisabled)}
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Sticky Footer */}
        <Box
          sx={{
            position: 'sticky',
            backgroundColor: 'white',
            pt: '15px',
            borderTop: '1px solid #E0E0E0',
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 10,
          }}
        >
          <Button sx={styles.actionSectionButton} onClick={handleApply}>
            Apply
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default InputEditModal;
